import React, { useState, useEffect, useCallback } from 'react';
import { format, addHours } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import {
  Users, Play, RefreshCw, Sun, UtensilsCrossed, User, AlertTriangle,
  Clock, ArrowRight, Calendar, ClipboardList, AlertCircle, Inbox,
  StickyNote, Send, ExternalLink, Gavel, Loader2,
} from 'lucide-react';
import {
  executiveAssignmentsApi, rhythmApi, authApi, workItemsApi, calendarApi,
  dailyPlanApi, reportingApi, decisionsApi,
} from '../api/services';
import type {
  ExecutiveAssignment, StartMyDayResponse, RestaurantResult, WorkItem,
  CalendarEvent, DailyPlan, EaActivityEntry,
} from '../types/models';
import RestaurantSearchDialog from '../components/RestaurantSearchDialog';
import { usePrincipal } from '../context/PrincipalContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { LinearProgressBar } from '@/components/ui/linear-progress';
import { useToast } from '@/components/ui/toast';
import { cn } from '@/lib/utils';

// ---------------------------------------------------------------------------
// EAWorkspacePage — multi-principal cockpit for an Executive Assistant.
// Top: switcher grid of principal cards with summary badges. Below:
// detailed panel for the active principal with quick actions, today's
// overview, overflow triage, handoff notes editor, queue-decision dialog,
// EA activity log.
// ---------------------------------------------------------------------------

interface UserInfo {
  id: string;
  email: string;
  displayName: string;
  role: string;
  isActive: boolean;
  isTenantAdmin: boolean;
}

interface PrincipalSummary {
  planStatus: string | null;
  overdueCount: number;
  upcomingEventsCount: number;
  eaActionCount: number;
  carryForwardCount: number;
  capacityRemainingHours: number;
  isOverloaded: boolean;
  nextMeeting: string | null;
  nextMeetingTime: string | null;
  overflowItems: WorkItem[];
}

interface ExecutiveState {
  assignment: ExecutiveAssignment;
  user: UserInfo | null;
  summary: PrincipalSummary | null;
  dayData: StartMyDayResponse | null;
  loading: boolean;
  error: string | null;
}

const PLAN_STATUS_TONE: Record<string, string> = {
  Active: 'border-emerald-500/60 text-emerald-700 dark:text-emerald-400',
  Approved: 'border-blue-500/60 text-blue-700 dark:text-blue-400',
  Proposed: 'border-amber-500/60 text-amber-700 dark:text-amber-400',
  Closed: 'border-border text-muted-foreground',
};

function planTone(status: string | null | undefined): string {
  return PLAN_STATUS_TONE[status ?? ''] || 'border-border text-muted-foreground';
}

function priorityTone(priority: string): string {
  switch (priority) {
    case 'Critical': return 'border-rose-500/60 text-rose-700 dark:text-rose-400';
    case 'High': return 'border-amber-500/60 text-amber-700 dark:text-amber-400';
    default: return 'border-border text-muted-foreground';
  }
}

export default function EAWorkspacePage() {
  const navigate = useNavigate();
  const { setActivePrincipal: setGlobalPrincipal } = usePrincipal();
  const { toast } = useToast();
  const [executives, setExecutives] = useState<ExecutiveState[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [users, setUsers] = useState<UserInfo[]>([]);
  const [activePrincipalId, setActivePrincipalId] = useState<string | null>(null);
  const [restaurantDialogOpen, setRestaurantDialogOpen] = useState(false);
  const [, setSelectedRestaurant] = useState<RestaurantResult | null>(null);
  const [handoffNotes, setHandoffNotes] = useState<string>('');
  const [handoffSaving, setHandoffSaving] = useState(false);
  const [handoffSaved, setHandoffSaved] = useState(false);
  const [preparingDay, setPreparingDay] = useState(false);
  const [dayPrepared, setDayPrepared] = useState(false);
  const [eaActivity, setEaActivity] = useState<EaActivityEntry[]>([]);
  const [eaActivityLoading, setEaActivityLoading] = useState(false);
  const [queueDecisionOpen, setQueueDecisionOpen] = useState(false);
  const [queueDecisionForm, setQueueDecisionForm] = useState({ title: '', rationale: '', stakeholders: '' });
  const [queueDecisionSaving, setQueueDecisionSaving] = useState(false);
  const today = format(new Date(), 'yyyy-MM-dd');

  const handleRestaurantSelect = (restaurant: RestaurantResult) => {
    setSelectedRestaurant(restaurant);
    window.location.href = `/calendar`;
  };

  const loadPrincipalSummary = useCallback(async (execUserId: string): Promise<PrincipalSummary | null> => {
    try {
      const [todayItems, calEvents, plan] = await Promise.all([
        workItemsApi.getByDate(today, execUserId).catch(() => [] as WorkItem[]),
        calendarApi.getByDate(today, execUserId).catch(() => [] as CalendarEvent[]),
        dailyPlanApi.getByDate(today, execUserId).catch(() => null as DailyPlan | null),
      ]);

      const todayDate = new Date(today);
      const overdueCount = todayItems.filter(i =>
        new Date(i.targetDate) < todayDate &&
        !['Done', 'Cancelled'].includes(i.status)
      ).length;

      const carryForwardCount = todayItems.filter(i => i.isCarryForward).length;

      const now = new Date();
      const next24h = addHours(now, 24);
      const upcomingEventsCount = calEvents.filter(e => {
        const start = new Date(e.startTime);
        return start >= now && start <= next24h;
      }).length;

      const upcomingMeetings = calEvents
        .filter(e => !e.isAllDay && new Date(e.startTime) > now)
        .sort((a, b) => a.startTime.localeCompare(b.startTime));
      const nextMeeting = upcomingMeetings.length > 0 ? upcomingMeetings[0].title : null;
      const nextMeetingTime = upcomingMeetings.length > 0 ? upcomingMeetings[0].startTime : null;

      const eaActionCount = todayItems.filter(i =>
        (i.status === 'Backlog' && !i.assignedToExecutiveId) ||
        (i.status === 'Planned' && !i.assignedToExecutiveId)
      ).length;

      const meetingMinutes = calEvents
        .filter(e => !e.isAllDay)
        .reduce((sum, e) => {
          const start = new Date(e.startTime);
          const end = new Date(e.endTime);
          return sum + Math.max(0, (end.getTime() - start.getTime()) / 60000);
        }, 0);
      const plannedMinutes = todayItems
        .filter(i => !['Done', 'Cancelled'].includes(i.status))
        .reduce((sum, i) => sum + (i.estimatedMinutes || 0), 0);
      const totalWorkingMinutes = 8 * 60;
      const capacityRemainingHours = Math.max(0, Math.round((totalWorkingMinutes - meetingMinutes - plannedMinutes) / 60 * 10) / 10);
      const isOverloaded = (meetingMinutes + plannedMinutes) > totalWorkingMinutes;
      const overflowItems = isOverloaded
        ? todayItems.filter(i => !['Done', 'Cancelled'].includes(i.status)).slice(-3)
        : [];

      return {
        planStatus: plan?.status || null,
        overdueCount,
        upcomingEventsCount,
        eaActionCount,
        carryForwardCount,
        capacityRemainingHours,
        isOverloaded,
        nextMeeting,
        nextMeetingTime,
        overflowItems,
      };
    } catch {
      return null;
    }
  }, [today]);

  useEffect(() => {
    const load = async () => {
      try {
        const [assignments, usersData] = await Promise.all([
          executiveAssignmentsApi.getAll(),
          authApi.getUsers(),
        ]);
        setUsers(usersData);
        const activeAssignments = assignments.filter(a => a.isActive);
        const execStates: ExecutiveState[] = activeAssignments.map(a => ({
          assignment: a,
          user: usersData.find(u => u.id === a.executiveUserId) || null,
          summary: null,
          dayData: null,
          loading: false,
          error: null,
        }));
        setExecutives(execStates);

        if (activeAssignments.length > 0 && !activePrincipalId) {
          setActivePrincipalId(activeAssignments[0].executiveUserId);
        }

        const summaries = await Promise.all(
          activeAssignments.map(a => loadPrincipalSummary(a.executiveUserId))
        );
        setExecutives(prev => prev.map((e, i) => ({ ...e, summary: summaries[i] })));
      } catch (err: any) {
        setError(err.response?.data?.message || err.message || 'Failed to load assignments');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleAction = async (index: number, action: 'start' | 'replan' | 'planTomorrow') => {
    setExecutives(prev => prev.map((e, i) => i === index ? { ...e, loading: true, error: null } : e));
    try {
      const execUserId = executives[index].assignment.executiveUserId;
      let data: StartMyDayResponse;
      if (action === 'start') {
        data = await rhythmApi.startMyDay({ forExecutiveUserId: execUserId });
      } else if (action === 'replan') {
        data = await rhythmApi.replan({ forExecutiveUserId: execUserId });
      } else {
        data = await rhythmApi.planTomorrow({ forExecutiveUserId: execUserId });
      }
      setExecutives(prev => prev.map((e, i) => i === index ? { ...e, dayData: data, loading: false } : e));
      const newSummary = await loadPrincipalSummary(execUserId);
      setExecutives(prev => prev.map((e, i) => i === index ? { ...e, summary: newSummary } : e));
    } catch (err: any) {
      setExecutives(prev => prev.map((e, i) => i === index
        ? { ...e, loading: false, error: err.response?.data?.message || err.message || 'Action failed' }
        : e
      ));
    }
  };

  const handlePrincipalClick = (execUserId: string) => {
    setActivePrincipalId(execUserId);
    setDayPrepared(false);
    setHandoffNotes('');
    setHandoffSaved(false);
  };

  const handleViewAsPrincipal = (execUserId: string) => {
    const exec = executives.find(e => e.assignment.executiveUserId === execUserId);
    if (exec) {
      setGlobalPrincipal({
        id: execUserId,
        name: exec.user?.displayName || 'Unknown Executive',
        email: exec.user?.email || '',
        isTenantAdmin: exec.user?.isTenantAdmin ?? false,
      });
      navigate('/');
    }
  };

  const activeExecIndex = executives.findIndex(e => e.assignment.executiveUserId === activePrincipalId);
  const activeExec = activeExecIndex >= 0 ? executives[activeExecIndex] : null;

  const handlePrepareDay = async () => {
    if (!activePrincipalId || activeExecIndex < 0) return;
    setPreparingDay(true);
    try {
      const data = await rhythmApi.startMyDay({ forExecutiveUserId: activePrincipalId });
      setExecutives(prev => prev.map((e, i) => i === activeExecIndex ? { ...e, dayData: data } : e));
      setDayPrepared(true);
      const newSummary = await loadPrincipalSummary(activePrincipalId);
      setExecutives(prev => prev.map((e, i) => i === activeExecIndex ? { ...e, summary: newSummary } : e));
    } catch (err: any) {
      setExecutives(prev => prev.map((e, i) => i === activeExecIndex
        ? { ...e, error: err.response?.data?.message || err.message || 'Failed to prepare day' }
        : e
      ));
    } finally {
      setPreparingDay(false);
    }
  };

  const handleSaveHandoffNotes = async () => {
    if (!activePrincipalId) return;
    setHandoffSaving(true);
    try {
      await dailyPlanApi.updateHandoffNotes(today, handoffNotes, activePrincipalId);
      setHandoffSaved(true);
      setTimeout(() => setHandoffSaved(false), 3000);
    } catch (err: any) {
      console.error('Failed to save handoff notes', err);
      toast({ variant: 'error', message: err?.response?.data?.error ?? 'Failed to save handoff notes' });
    } finally {
      setHandoffSaving(false);
    }
  };

  useEffect(() => {
    if (!activePrincipalId) return;
    dailyPlanApi.getByDate(today, activePrincipalId)
      .then((plan) => {
        setHandoffNotes(plan?.handoffNotes || '');
        setDayPrepared(!!plan?.preparedByUserId);
      })
      .catch(() => {
        setHandoffNotes('');
        setDayPrepared(false);
      });
  }, [activePrincipalId, today]);

  const handleQueueDecision = useCallback(async () => {
    if (!activePrincipalId || !queueDecisionForm.title.trim()) return;
    setQueueDecisionSaving(true);
    try {
      await decisionsApi.queue({
        title: queueDecisionForm.title,
        rationale: queueDecisionForm.rationale || undefined,
        stakeholders: queueDecisionForm.stakeholders || undefined,
      }, activePrincipalId);
      setQueueDecisionOpen(false);
      setQueueDecisionForm({ title: '', rationale: '', stakeholders: '' });
    } catch (err: any) {
      console.error('Failed to queue decision', err);
      toast({ variant: 'error', message: err?.response?.data?.error ?? 'Failed to queue decision' });
    } finally {
      setQueueDecisionSaving(false);
    }
  }, [activePrincipalId, queueDecisionForm, toast]);

  useEffect(() => {
    if (!activePrincipalId) return;
    setEaActivityLoading(true);
    const now = new Date();
    const weekAgo = new Date(now);
    weekAgo.setDate(now.getDate() - 7);
    const from = weekAgo.toISOString().slice(0, 10);
    const to = now.toISOString().slice(0, 10);
    reportingApi.getEaActivity(from, to, activePrincipalId)
      .then((data: EaActivityEntry[]) => setEaActivity(data))
      .catch(() => setEaActivity([]))
      .finally(() => setEaActivityLoading(false));
  }, [activePrincipalId]);

  const resolveGuidsInDetail = (detail: string | null): string => {
    if (!detail) return '-';
    return detail.replace(
      /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi,
      (guid) => {
        const matchedUser = users.find(u => u.id === guid);
        return matchedUser ? matchedUser.displayName : guid;
      }
    );
  };

  const formatMeetingTime = (iso: string): string => {
    try {
      return new Date(iso).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
    } catch { return iso; }
  };

  return (
    <div className="mx-auto max-w-[1280px] p-4 md:p-6">
      <div className="mb-6 flex items-center gap-2">
        <Users className="h-7 w-7 text-primary" />
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">EA Workspace</h1>
          <p className="text-sm text-muted-foreground">Multi-principal dashboard — manage your assigned executives</p>
        </div>
      </div>

      {loading && <LinearProgressBar className="mb-3" />}
      {error && (
        <Alert variant="error" className="mb-3">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {!loading && executives.length === 0 && (
        <Card>
          <CardContent className="py-12 text-center">
            <Users className="mx-auto mb-3 h-12 w-12 text-muted-foreground/50" />
            <p className="mb-1 text-lg font-semibold">No executive assignments found</p>
            <p className="text-sm text-muted-foreground">
              Contact your admin to get assigned to an executive.
            </p>
          </CardContent>
        </Card>
      )}

      {/* Principal switcher */}
      {executives.length > 0 && (
        <>
          <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            Your Principals ({executives.length})
          </p>
          <div className="mb-6 grid grid-cols-1 gap-3 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
            {executives.map((exec) => {
              const isActive = exec.assignment.executiveUserId === activePrincipalId;
              const s = exec.summary;
              return (
                <button
                  key={exec.assignment.id}
                  type="button"
                  onClick={() => handlePrincipalClick(exec.assignment.executiveUserId)}
                  className={cn(
                    'rounded-2xl border-2 text-left transition-colors',
                    isActive ? 'border-primary' : 'border-transparent hover:border-border',
                  )}
                >
                  <Card className="border-0">
                    <CardContent className="py-3">
                      <div className="mb-2 flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <p className="truncate text-sm font-bold">
                            {exec.user?.displayName || 'Unknown Executive'}
                          </p>
                          <p className="truncate text-xs text-muted-foreground">{exec.user?.email || ''}</p>
                        </div>
                        {isActive && (
                          <span className="inline-flex items-center rounded-full bg-primary/15 px-2 py-0.5 text-[11px] font-semibold text-primary">
                            Active
                          </span>
                        )}
                      </div>
                      <span
                        className={cn(
                          'mb-3 inline-flex items-center rounded-full border bg-transparent px-2 py-0.5 text-[11px] font-medium',
                          planTone(s?.planStatus),
                        )}
                      >
                        {s?.planStatus ? `Plan: ${s.planStatus}` : 'No plan'}
                      </span>
                      <div className="flex flex-wrap items-center gap-2">
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span className="flex items-center gap-1">
                              <AlertCircle
                                className={cn(
                                  'h-4 w-4',
                                  (s?.overdueCount ?? 0) > 0
                                    ? 'text-rose-600 dark:text-rose-400'
                                    : 'text-muted-foreground/50',
                                )}
                              />
                              <span className="text-xs tabular-nums">{s?.overdueCount ?? 0}</span>
                            </span>
                          </TooltipTrigger>
                          <TooltipContent>Overdue work items</TooltipContent>
                        </Tooltip>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span className="flex items-center gap-1">
                              <Calendar className="h-4 w-4 text-muted-foreground" />
                              <span className="text-xs tabular-nums">{s?.upcomingEventsCount ?? 0}</span>
                            </span>
                          </TooltipTrigger>
                          <TooltipContent>Upcoming events (24h)</TooltipContent>
                        </Tooltip>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span className="flex items-center gap-1">
                              <Inbox
                                className={cn(
                                  'h-4 w-4',
                                  (s?.eaActionCount ?? 0) > 0
                                    ? 'text-amber-600 dark:text-amber-400'
                                    : 'text-muted-foreground/50',
                                )}
                              />
                              <span className="text-xs tabular-nums">{s?.eaActionCount ?? 0}</span>
                            </span>
                          </TooltipTrigger>
                          <TooltipContent>Items requiring EA action</TooltipContent>
                        </Tooltip>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span
                              className={cn(
                                'inline-flex items-center rounded-full border bg-transparent px-2 py-0.5 text-[11px] font-semibold',
                                s?.isOverloaded
                                  ? 'border-rose-500/60 text-rose-600 dark:text-rose-400'
                                  : 'border-emerald-500/60 text-emerald-700 dark:text-emerald-400',
                              )}
                            >
                              {s?.capacityRemainingHours ?? '-'}h
                            </span>
                          </TooltipTrigger>
                          <TooltipContent>Capacity remaining</TooltipContent>
                        </Tooltip>
                      </div>
                    </CardContent>
                  </Card>
                </button>
              );
            })}
          </div>
        </>
      )}

      {/* Active principal detail */}
      {activeExec && (
        <Card>
          <CardContent>
            <div className="mb-3 flex items-center justify-between gap-2">
              <div>
                <p className="text-lg font-semibold">
                  {activeExec.user?.displayName || activeExec.assignment.executiveUserId}
                </p>
                <div className="flex items-center gap-2">
                  <p className="text-sm text-muted-foreground">{activeExec.user?.email || 'Executive'}</p>
                  {activeExec.user && (
                    <span className="inline-flex items-center rounded-full border border-border bg-transparent px-2 py-0.5 text-[11px] font-medium">
                      {activeExec.user.role}
                    </span>
                  )}
                </div>
              </div>
              <span
                className={cn(
                  'inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold',
                  activeExec.assignment.isActive
                    ? 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-400'
                    : 'bg-muted text-muted-foreground',
                )}
              >
                {activeExec.assignment.isActive ? 'Active Assignment' : 'Inactive'}
              </span>
            </div>

            <hr className="mb-4 border-border" />

            {/* Summary chips */}
            {activeExec.summary && (
              <div className="mb-4 flex flex-wrap gap-2">
                <span className={cn('inline-flex items-center rounded-full border bg-transparent px-2 py-0.5 text-[11px] font-medium', planTone(activeExec.summary.planStatus))}>
                  {activeExec.summary.planStatus ? `Plan: ${activeExec.summary.planStatus}` : 'No plan today'}
                </span>
                <span className="inline-flex items-center gap-1 rounded-full border border-border bg-transparent px-2 py-0.5 text-[11px] font-medium">
                  <Clock className="h-3 w-3" />
                  {activeExec.summary.nextMeeting
                    ? `Next: ${activeExec.summary.nextMeeting} at ${formatMeetingTime(activeExec.summary.nextMeetingTime!)}`
                    : 'No upcoming meetings'}
                </span>
                {activeExec.summary.overdueCount > 0 && (
                  <span className="inline-flex items-center gap-1 rounded-full border border-rose-500/60 bg-transparent px-2 py-0.5 text-[11px] font-medium text-rose-600 dark:text-rose-400">
                    <AlertCircle className="h-3 w-3" />
                    {activeExec.summary.overdueCount} overdue
                  </span>
                )}
                {activeExec.summary.carryForwardCount > 0 && (
                  <span className="inline-flex items-center gap-1 rounded-full border border-amber-500/60 bg-transparent px-2 py-0.5 text-[11px] font-medium text-amber-700 dark:text-amber-400">
                    <ArrowRight className="h-3 w-3" />
                    {activeExec.summary.carryForwardCount} carried forward
                  </span>
                )}
                <span
                  className={cn(
                    'inline-flex items-center rounded-full border bg-transparent px-2 py-0.5 text-[11px] font-medium',
                    activeExec.summary.isOverloaded
                      ? 'border-rose-500/60 text-rose-600 dark:text-rose-400'
                      : 'border-emerald-500/60 text-emerald-700 dark:text-emerald-400',
                  )}
                >
                  {activeExec.summary.capacityRemainingHours}h remaining
                </span>
                {activeExec.summary.eaActionCount > 0 && (
                  <span className="inline-flex items-center gap-1 rounded-full border border-amber-500/60 bg-transparent px-2 py-0.5 text-[11px] font-medium text-amber-700 dark:text-amber-400">
                    <Inbox className="h-3 w-3" />
                    {activeExec.summary.eaActionCount} need EA action
                  </span>
                )}
                {activeExec.summary.upcomingEventsCount > 0 && (
                  <span className="inline-flex items-center gap-1 rounded-full border border-border bg-transparent px-2 py-0.5 text-[11px] font-medium">
                    <Calendar className="h-3 w-3" />
                    {activeExec.summary.upcomingEventsCount} events (24h)
                  </span>
                )}
              </div>
            )}

            {/* Prepared-by alert */}
            {dayPrepared && activeExec.summary?.planStatus && (
              <Alert variant="info" className="mb-4">
                <AlertDescription className="flex items-start gap-2">
                  <Send className="mt-0.5 h-4 w-4 shrink-0" />
                  <span>
                    This plan was prepared by an EA and is currently in <strong>{activeExec.summary.planStatus}</strong> status.
                  </span>
                </AlertDescription>
              </Alert>
            )}

            {/* Quick actions */}
            <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Quick Actions</p>
            <div className="mb-4 flex flex-wrap gap-2">
              <Button
                size="sm"
                variant="secondary"
                onClick={() => handleViewAsPrincipal(activeExec.assignment.executiveUserId)}
                className="gap-1.5"
              >
                <ExternalLink className="h-3.5 w-3.5" />
                View as {activeExec.user?.displayName?.split(' ')[0] || 'Principal'}
              </Button>
              <Button
                size="sm"
                onClick={handlePrepareDay}
                disabled={preparingDay || activeExec.loading}
                className="gap-1.5"
              >
                {preparingDay ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
                {preparingDay ? 'Preparing...' : 'Prepare Day'}
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => handleAction(activeExecIndex, 'start')}
                disabled={activeExec.loading}
                className="gap-1.5"
              >
                <Play className="h-3.5 w-3.5" />
                Start Their Day
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => handleAction(activeExecIndex, 'replan')}
                disabled={activeExec.loading}
                className="gap-1.5"
              >
                <RefreshCw className="h-3.5 w-3.5" />
                Replan
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => handleAction(activeExecIndex, 'planTomorrow')}
                disabled={activeExec.loading}
                className="gap-1.5"
              >
                <Sun className="h-3.5 w-3.5" />
                Plan Tomorrow
              </Button>
              <Button size="sm" variant="outline" onClick={() => navigate('/calendar')} className="gap-1.5">
                <Calendar className="h-3.5 w-3.5" />
                View Calendar
              </Button>
              <Button size="sm" variant="outline" onClick={() => navigate('/work-items')} className="gap-1.5">
                <ClipboardList className="h-3.5 w-3.5" />
                View Work Items
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => navigate(`/principal-profile/${activeExec.assignment.executiveUserId}`)}
                className="gap-1.5"
              >
                <User className="h-3.5 w-3.5" />
                Principal Profile
              </Button>
              <Button size="sm" variant="outline" onClick={() => setRestaurantDialogOpen(true)} className="gap-1.5">
                <UtensilsCrossed className="h-3.5 w-3.5" />
                Book Restaurant
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => setQueueDecisionOpen(true)}
                className="gap-1.5 text-amber-700 dark:text-amber-400"
              >
                <Gavel className="h-3.5 w-3.5" />
                Queue Decision
              </Button>
            </div>

            {/* Handoff notes */}
            {dayPrepared && (
              <div className="mb-4 rounded-lg border border-border bg-muted/10 p-3">
                <div className="mb-2 flex items-center gap-2">
                  <StickyNote className="h-4 w-4 text-primary" />
                  <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                    Handoff Notes for Executive
                  </p>
                </div>
                <Textarea
                  rows={3}
                  value={handoffNotes}
                  onChange={(e) => { setHandoffNotes(e.target.value); setHandoffSaved(false); }}
                  placeholder="Add notes for the executive about today's plan, priorities, things to watch for..."
                />
                <div className="mt-2 flex items-center gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={handleSaveHandoffNotes}
                    disabled={handoffSaving}
                  >
                    {handoffSaving ? 'Saving...' : 'Save Notes'}
                  </Button>
                  {handoffSaved && (
                    <p className="text-xs text-emerald-600 dark:text-emerald-400">Saved</p>
                  )}
                </div>
              </div>
            )}

            {activeExec.loading && <LinearProgressBar className="mb-3" />}
            {activeExec.error && (
              <Alert variant="error" className="mb-3">
                <AlertDescription>{activeExec.error}</AlertDescription>
              </Alert>
            )}

            {/* Today's overview */}
            {activeExec.dayData && (
              <div>
                <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Today's Overview</p>
                <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                  <div className="rounded-lg bg-muted/30 p-3">
                    <p className="text-xs text-muted-foreground">Available</p>
                    <p className="text-lg font-bold tabular-nums">
                      {Math.round(activeExec.dayData.capacity.availableMinutes / 60 * 10) / 10}h
                    </p>
                  </div>
                  <div className="rounded-lg bg-muted/30 p-3">
                    <p className="text-xs text-muted-foreground">Meetings</p>
                    <p className="text-lg font-bold tabular-nums">
                      {Math.round(activeExec.dayData.capacity.meetingMinutes / 60 * 10) / 10}h
                    </p>
                  </div>
                  <div className="rounded-lg bg-muted/30 p-3">
                    <p className="text-xs text-muted-foreground">Planned</p>
                    <p className="text-lg font-bold tabular-nums">
                      {Math.round(activeExec.dayData.capacity.plannedMinutes / 60 * 10) / 10}h
                    </p>
                  </div>
                  <div className="rounded-lg bg-muted/30 p-3">
                    <p className="text-xs text-muted-foreground">Utilisation</p>
                    <p className="text-lg font-bold tabular-nums">
                      {Math.round(activeExec.dayData.capacity.utilisationPercent)}%
                    </p>
                  </div>
                </div>
                <div className="mt-3 flex flex-wrap gap-2">
                  <span
                    className={cn(
                      'inline-flex items-center rounded-full border bg-transparent px-2 py-0.5 text-[11px] font-medium',
                      activeExec.dayData.plan.status === 'Active'
                        ? 'border-emerald-500/60 text-emerald-700 dark:text-emerald-400'
                        : 'border-border text-muted-foreground',
                    )}
                  >
                    Plan: {activeExec.dayData.plan.status}
                  </span>
                  {activeExec.dayData.plan.isOverloaded && (
                    <span className="inline-flex items-center rounded-full border border-rose-500/60 bg-transparent px-2 py-0.5 text-[11px] font-medium text-rose-600 dark:text-rose-400">
                      Overloaded
                    </span>
                  )}
                  {activeExec.dayData.warnings.length > 0 && (
                    <span className="inline-flex items-center rounded-full border border-amber-500/60 bg-transparent px-2 py-0.5 text-[11px] font-medium text-amber-700 dark:text-amber-400">
                      {activeExec.dayData.warnings.length} warnings
                    </span>
                  )}
                </div>

                {/* Overflow triage */}
                {activeExec.dayData.plan.isOverloaded && activeExec.dayData.overflowItems.length > 0 && (
                  <div className="mt-4 rounded-lg border border-rose-500 bg-rose-500/[0.04] p-3">
                    <div className="mb-1 flex items-center gap-1">
                      <AlertTriangle className="h-4 w-4 text-rose-600 dark:text-rose-400" />
                      <p className="text-sm font-semibold text-rose-600 dark:text-rose-400">
                        Overflow Triage ({activeExec.dayData.overflowItems.length} items)
                      </p>
                    </div>
                    <p className="mb-2 text-xs text-muted-foreground">
                      This executive's day is overloaded. The following items exceed available capacity:
                    </p>
                    <div className="flex flex-col gap-1">
                      {activeExec.dayData.overflowItems.map(item => (
                        <div key={item.id} className="flex items-center gap-2 py-1">
                          <span className={cn('inline-flex items-center rounded-full border bg-transparent px-1.5 py-0.5 text-[10px] font-medium', priorityTone(item.priority))}>
                            {item.priority}
                          </span>
                          <p className="flex-1 text-sm">{item.title}</p>
                          <p className="text-xs text-muted-foreground tabular-nums">{item.estimatedMinutes}m</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* EA Activity Log */}
      {activeExec && (
        <Card className="mt-4">
          <CardContent>
            <h2 className="mb-1 text-lg font-semibold">EA Activity Log</h2>
            <p className="mb-3 text-sm text-muted-foreground">
              Actions performed by EAs on behalf of {activeExec.user?.displayName || 'this executive'} (last 7 days)
            </p>
            {eaActivityLoading && <LinearProgressBar className="mb-3" />}
            {!eaActivityLoading && eaActivity.length > 0 && (
              <div className="max-h-[320px] overflow-auto rounded-lg border border-border">
                <table className="w-full text-sm">
                  <thead className="sticky top-0 bg-muted/80 backdrop-blur">
                    <tr className="border-b border-border text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                      <th className="px-3 py-2">Time</th>
                      <th className="px-3 py-2">EA</th>
                      <th className="px-3 py-2">Action</th>
                      <th className="px-3 py-2">Detail</th>
                    </tr>
                  </thead>
                  <tbody>
                    {eaActivity.slice(0, 50).map(entry => (
                      <tr key={entry.id} className="border-b border-border last:border-b-0">
                        <td className="whitespace-nowrap px-3 py-2 text-xs text-muted-foreground">
                          {new Date(entry.timestamp).toLocaleString('en-GB', {
                            day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit',
                          })}
                        </td>
                        <td className="px-3 py-2">{entry.eaName || 'Unknown'}</td>
                        <td className="px-3 py-2">
                          <span className="inline-flex items-center rounded-full border border-border bg-transparent px-2 py-0.5 text-[11px] font-medium">
                            {entry.action}
                          </span>
                        </td>
                        <td className="max-w-[300px] truncate px-3 py-2 text-xs text-muted-foreground">
                          {resolveGuidsInDetail(entry.detail)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            {!eaActivityLoading && eaActivity.length === 0 && (
              <p className="py-6 text-center text-sm text-muted-foreground">
                No EA activity recorded for this period
              </p>
            )}
          </CardContent>
        </Card>
      )}

      <RestaurantSearchDialog
        open={restaurantDialogOpen}
        onClose={() => setRestaurantDialogOpen(false)}
        onSelect={handleRestaurantSelect}
      />

      {/* Queue Decision dialog */}
      <Dialog open={queueDecisionOpen} onOpenChange={(o) => !o && setQueueDecisionOpen(false)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Queue Decision for Executive</DialogTitle>
          </DialogHeader>
          <div className="flex flex-col gap-3">
            <Alert variant="info">
              <AlertDescription>
                This will create a pending decision for {activeExec?.user?.displayName || 'the executive'} to approve, reject, or defer.
              </AlertDescription>
            </Alert>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="qd-title">Decision Title <span className="text-destructive">*</span></Label>
              <Input
                id="qd-title"
                value={queueDecisionForm.title}
                onChange={(e) => setQueueDecisionForm(prev => ({ ...prev, title: e.target.value }))}
                placeholder="e.g. Approve Q3 budget reallocation"
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="qd-rationale">Rationale</Label>
              <Textarea
                id="qd-rationale"
                rows={3}
                value={queueDecisionForm.rationale}
                onChange={(e) => setQueueDecisionForm(prev => ({ ...prev, rationale: e.target.value }))}
                placeholder="Why is this decision needed? What are the options?"
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="qd-stake">Stakeholders</Label>
              <Input
                id="qd-stake"
                value={queueDecisionForm.stakeholders}
                onChange={(e) => setQueueDecisionForm(prev => ({ ...prev, stakeholders: e.target.value }))}
                placeholder="Comma-separated names (e.g. CFO, VP Sales)"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setQueueDecisionOpen(false)}>Cancel</Button>
            <Button
              onClick={handleQueueDecision}
              disabled={queueDecisionSaving || !queueDecisionForm.title.trim()}
              className="gap-1.5"
            >
              {queueDecisionSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Gavel className="h-4 w-4" />}
              {queueDecisionSaving ? 'Queueing...' : 'Queue Decision'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
